import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePressAction } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { usePresence } from '../hooks/usePresence'; // [FLUID-1 2026-10-12]
import { fluid } from '../styles/motion'; // [FLUID-1 2026-10-12]
import { fluidPop, fluidCascade } from './Fluid'; // [FLUID-1 2026-10-12]
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26] the calendar is placed in page px
import { lgPopover, lgControl, lgPress, lgRim, lgControlBg, lgControlHover, lgCardSheen, lgGreen, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]

/**
 * DateRangeCalendar — a compact, premium date-RANGE picker in the app's glass
 * theme. Pick a start, then an end; the selected span shows a continuous shaded
 * band with distinct rounded start/end caps (the standard range-picker selection
 * design). There is NO apply/cancel — the second click commits the range live and
 * closes. A single click that is then re-clicked on the same day is a single-day
 * range. `start === end` renders as one selected day.
 *
 * The popover is portaled to <body> and positioned `fixed` under the trigger so
 * it's never clipped by an `overflow` ancestor or mis-centred by the PageFade
 * transform. It closes on outside-click, Esc, scroll and resize.
 */

interface Props {
  /** Inclusive range, YYYY-MM-DD. */
  start: string;
  end: string;
  /** Selectable bounds (inclusive). Days outside are disabled. */
  min?: string | null;
  max?: string | null;
  onChange: (start: string, end: string) => void;
  /** Applied to the trigger button so callers can resize it (e.g. styled()). */
  className?: string;
  /** [LIQUID-GLASS 2026-11-02] true = the button is a glass capsule (Dialing Report). Default: plain. */
  liquid?: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');
const toKey = (y: number, m0: number, d: number) => `${y}-${pad(m0 + 1)}-${pad(d)}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function parseKey(s: string): { y: number; m0: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!m) return null;
  return { y: Number(m[1]), m0: Number(m[2]) - 1, d: Number(m[3]) };
}

/** "2026-06-05" → "Jun 5, 2026". */
function fmtDay(s: string): string {
  const p = parseKey(s);
  if (!p) return '';
  return `${MONTHS[p.m0].slice(0, 3)} ${p.d}, ${p.y}`;
}

/** Trigger label: a single day, or "Jun 5 – Jun 30, 2026" (year shown once when shared). */
function fmtRange(start: string, end: string): string {
  const a = parseKey(start);
  const b = parseKey(end);
  if (!a || !b) return 'Select dates';
  if (start === end) return fmtDay(start);
  const aShort = `${MONTHS[a.m0].slice(0, 3)} ${a.d}`;
  const bShort = `${MONTHS[b.m0].slice(0, 3)} ${b.d}`;
  return a.y === b.y ? `${aShort} – ${bShort}, ${b.y}` : `${aShort}, ${a.y} – ${bShort}, ${b.y}`;
}

/* ───────────────────────── trigger ───────────────────────── */

const Trigger = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $liquid?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.75)')};
  color: ${(p) => p.theme.text};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  i.cal { color: ${(p) => p.theme.greenInk}; font-size: 13px; } /* [GREEN-ON-DARK 2026-08-27] */
  i.chev { color: ${(p) => p.theme.muted}; font-size: 11px; margin-left: 2px; }

  &:hover { border-color: ${(p) => p.theme.green600}; }
  &[aria-expanded='true'] {
    border-color: ${(p) => p.theme.green600};
    box-shadow: 0 0 0 3px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)')};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: 10 px sides and 8 px gaps (was 12 / 10), the same trim as the single-date picker,
     and when a page stretches the trigger across its cell the arrow sits at the right edge like a dropdown's (the range
     text is already one line). Desktop unchanged. */
  @media (max-width: 768px) {
    padding: 0 10px;
    gap: 8px;
    i.chev { margin-left: auto; }
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${(p) => p.$liquid && css`
    position: relative;
    isolation: isolate;
    overflow: hidden;
    border: none;
    border-radius: 999px;
    padding: 0 14px;
    background-color: ${lgControlBg(p)};
    background-image: ${lgCardSheen(p)};
    & > * { position: relative; z-index: 1; }
    ${lgRim}
    ${lgPress}
    box-shadow: ${p.theme.colorScheme === 'dark'
      ? '0 1px 1px rgba(0,0,0,0.25), 0 10px 24px -12px rgba(0,0,0,0.6)'
      : '0 0 0 1px rgba(22,25,23,0.13), 0 1px 2px rgba(22,25,23,0.06), 0 8px 18px -10px rgba(22,25,23,0.22)'};
    &:hover:not(:disabled) { background-color: ${lgControlHover(p)}; }
    &[aria-expanded='true'] {
      box-shadow: 0 0 0 1px color-mix(in srgb, ${lgGreen(p)} 70%, transparent), 0 0 0 4px color-mix(in srgb, ${lgGreen(p)} 16%, transparent);
    }
    @media (max-width: 768px) { padding: 0 10px; }
  `}
`;

/* ───────────────────────── popover ───────────────────────── */

const Pop = styled.div<{ $open: boolean }>`
  position: fixed;
  z-index: 1300;
  width: 312px;
  /* [PHONE-LAYOUT 2026-10-22] never wider than a phone screen less 8 px each side (the placement clamps it there) */
  @media (max-width: 768px) {
    width: min(312px, calc(100vw - 16px));
  }
  padding: 14px;
  border-radius: 18px;
  /* [LIQUID-GLASS 2026-11-02, user] Glass with the light top edge (styles/liquidGlass.ts), like the one-day calendar.
     [STALE-PRESS 2026-11-02, user] Without the live blur (lgPopover): it opens next to buttons and scrolls. */
  ${lgPopover}
  /* [RESPONSIVE 2026-07-07] Cap to the viewport height (max-height set inline from
     the trigger position) and scroll internally, so the calendar never runs off
     the bottom on a short screen. */
  overflow-y: auto;
  overflow-x: hidden;
  /* [FLUID-1 2026-10-12, user] Grows down out of the trigger's right corner on the spring, the summary,
     the month bar and the grid settling in one after another (components/Fluid.tsx); the close plays
     before it unmounts. Replaces the old 0.16 s pop-in. */
  ${(p) => fluidPop({ open: p.$open, origin: '100% 0', from: 'translateY(-8px) scale(0.94)' })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 12) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

/* Selected-range summary so both ends are always legible, even mid-selection. */
const Summary = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.09)')}; /* [LIQUID-GLASS 2026-11-02] hairline */
`;

const Pillbox = styled.div<{ $muted?: boolean }>`
  flex: 1;
  min-width: 0;
  text-align: center;
  padding: 6px 8px;
  border-radius: 999px; /* [LIQUID-GLASS 2026-11-02] a capsule pressed into the glass */
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => (p.$muted ? p.theme.muted : p.theme.text)};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border: 1px solid transparent;
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')};
`;

const Arrow = styled.span`
  flex-shrink: 0;
  color: ${(p) => p.theme.muted};
  font-size: 11px;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const HeadLabel = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
`;

/* [LIQUID-GLASS 2026-11-02] The month arrows: round glass buttons (squish + press light), like the panels' close button. */
const NavBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgControl}
  ${lgPress}
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: none;
  color: ${(p) => p.theme.text};
  font-size: 11px;
  cursor: pointer;

  &:hover:not(:disabled) { color: ${(p) => p.theme.green600}; }
  &:disabled { opacity: 0.32; cursor: not-allowed; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  /* No column gap so in-range days connect into a continuous band; a small row
     gap keeps the weeks visually separated (the band breaks at week edges). */
  column-gap: 0;
  row-gap: 4px;
`;

const Wk = styled.div`
  text-align: center;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${(p) => p.theme.muted};
  padding: 4px 0 6px;
`;

const Blank = styled.div`
  height: 36px;
`;

/* A day cell. Two background layers behind the number:
   ::before = the light range band (full width mid-range, half-width at the caps so
              it only reaches toward the inside of the range);
   ::after  = the solid green start/end/single cap pill (inset → reads as a pill). */
const Day = styled.button<{
  $cap?: boolean;        // start, end, or single — gets the solid pill
  $capLeft?: boolean;    // pill rounded on the left (range start)
  $capRight?: boolean;   // pill rounded on the right (range end)
  $inRange?: boolean;    // strictly inside the range — band only
  $bandLeft?: boolean;   // band extends to the left edge (toward an earlier in-range day)
  $bandRight?: boolean;  // band extends to the right edge
  $today?: boolean;
  $disabled?: boolean;
}>`
  position: relative;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 12.5px;
  font-weight: ${(p) => (p.$cap ? 700 : 500)};
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.26 : 1)};
  color: ${(p) =>
    p.$disabled ? p.theme.muted : p.$cap ? '#fff' : p.theme.text};

  /* range band */
  &::before {
    content: '';
    position: absolute;
    top: 3px;
    bottom: 3px;
    left: ${(p) => (p.$bandLeft ? '0' : '50%')};
    right: ${(p) => (p.$bandRight ? '0' : '50%')};
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.16)' : 'rgba(26,159,125,0.12)')};
    display: ${(p) => (p.$inRange || (p.$cap && (p.$bandLeft || p.$bandRight)) ? 'block' : 'none')};
    z-index: 0;
  }

  /* solid cap pill (start / end / single) */
  &::after {
    content: '';
    position: absolute;
    inset: 2px;
    border-radius: ${(p) =>
      p.$capLeft && p.$capRight ? '9px' : p.$capLeft ? '9px 7px 7px 9px' : p.$capRight ? '7px 9px 9px 7px' : '9px'};
    /* [LIQUID-GLASS 2026-11-02] glossy: a soft shine over the top half and a light top edge */
    background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%),
      linear-gradient(180deg, ${(p) => p.theme.green600}, ${(p) => p.theme.green700});
    box-shadow: 0 2px 6px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.35)' : 'rgba(20, 107, 84, 0.35)')},
      inset 0 1px 0 rgba(255,255,255,0.45);
    display: ${(p) => (p.$cap ? 'block' : 'none')};
    z-index: 0;
  }

  /* today ring when the day isn't itself a selected cap */
  box-shadow: ${(p) =>
    p.$today && !p.$cap ? `inset 0 0 0 1.5px ${p.theme.green600}` : 'none'};
  border-radius: 9px;

  & > span {
    position: relative;
    z-index: 1;
  }

  &:hover > span {
    /* hover affordance for non-cap, enabled days */
    color: ${(p) => (p.$disabled || p.$cap ? undefined : p.theme.greenInk)}; /* [GREEN-ON-DARK 2026-08-27] */
  }
`;

const DateRangeCalendar: React.FC<Props> = ({ start, end, min, max, onChange, className, liquid = false }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // [FLUID-8 2026-10-12, user] Opens on the mouse PRESS, like the dropdowns (hooks/usePressAction).
  const openPress = usePressAction<HTMLButtonElement>(() => setOpen((o) => !o));
  const popRef = useRef<HTMLDivElement>(null);

  // In-progress selection: `anchor` is the first click; `hover` previews the band
  // until the second click commits. null anchor → showing the committed range.
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);

  const [view, setView] = useState(() => {
    const p = parseKey(start);
    const now = new Date();
    return p ? { y: p.y, m0: p.m0 } : { y: now.getFullYear(), m0: now.getMonth() };
  });

  // Re-centre on the start month whenever the committed range changes / on open.
  useEffect(() => {
    const p = parseKey(start);
    if (p) setView({ y: p.y, m0: p.m0 });
  }, [start]);

  const todayKey = useMemo(() => {
    const n = new Date();
    return toKey(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  // [FLUID-1 2026-10-12] Mounted through the 200 ms close so the exit can play; `open` stays the truth.
  const pres = usePresence(open, fluid.outMs);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = pageRect(el); // [APP-FIT 2026-10-26] page px (utils/appZoom), like the top / left set below
    // [RESPONSIVE 2026-07-07] Right-align the popover to the trigger (its original
    // look) but clamp it fully inside the viewport so it can never clip off the
    // left/right edge on a narrow screen (mirrors GlassSelect's viewport-clamp).
    // The height is capped in the render (max-height from rect.top), so a short
    // screen scrolls the calendar internally instead of hiding its lower weeks.
    const margin = 8;
    const width = 312; // matches Pop's fixed width below
    let left = r.right - width;
    left = Math.min(left, pageViewport().width - width - margin);
    left = Math.max(left, margin);
    setRect({ top: r.bottom + 6, left });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // Close on outside-click / Esc; close on scroll+resize so it can't drift away.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onMove = (e: Event) => {
      // [RESPONSIVE 2026-07-07] A scroll INSIDE the popover (a short viewport made
      // the calendar internally scrollable via max-height) must NOT close it — only
      // a scroll of the page behind it should, since that detaches this fixed
      // popover from its trigger. Resize passes the window (not a Node) so it still
      // closes. Mirrors GlassSelect's inside-scroll guard.
      if (e.target instanceof Node && popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  // Reset any in-progress selection whenever the popover toggles.
  useEffect(() => {
    if (!open) { setAnchor(null); setHover(null); }
  }, [open]);

  const isDisabled = useCallback(
    (key: string) => {
      if (min && key < min) return true;
      if (max && key > max) return true;
      return false;
    },
    [min, max]
  );

  const monthIdx = (y: number, m0: number) => y * 12 + m0;
  const minP = min ? parseKey(min) : null;
  const maxP = max ? parseKey(max) : null;
  const prevDisabled = !!minP && monthIdx(view.y, view.m0) <= monthIdx(minP.y, minP.m0);
  const nextDisabled = !!maxP && monthIdx(view.y, view.m0) >= monthIdx(maxP.y, maxP.m0);

  const step = (dir: -1 | 1) => {
    setView((v) => {
      const d = new Date(v.y, v.m0 + dir, 1);
      return { y: d.getFullYear(), m0: d.getMonth() };
    });
  };

  const firstWeekday = new Date(view.y, view.m0, 1).getDay();
  const daysInMonth = new Date(view.y, view.m0 + 1, 0).getDate();

  // The range currently being shown: the committed [start, end], or the live
  // anchor→hover preview while a selection is in progress.
  const [rangeStart, rangeEnd] = useMemo(() => {
    if (anchor) {
      const other = hover || anchor;
      return anchor <= other ? [anchor, other] : [other, anchor];
    }
    return [start, end];
  }, [anchor, hover, start, end]);

  const pick = (key: string) => {
    if (isDisabled(key)) return;
    if (!anchor) {
      setAnchor(key);
      setHover(key);
    } else {
      const s = anchor <= key ? anchor : key;
      const e = anchor <= key ? key : anchor;
      setAnchor(null);
      setHover(null);
      setOpen(false);
      onChange(s, e);
    }
  };

  return (
    <>
      <Trigger
        $liquid={liquid}
        ref={triggerRef}
        type="button"
        className={className}
        aria-expanded={open}
        {...openPress} /* [FLUID-8 2026-10-12] on the press */
        title="Pick a date range"
      >
        <i className="cal fas fa-calendar-day" />
        {fmtRange(start, end)}
        <i className="chev fas fa-chevron-down" />
      </Trigger>

      {pres.mounted && rect && createPortal(
        <Pop ref={popRef} $open={open && pres.entered} style={{ top: rect.top, left: rect.left, maxHeight: `calc(100vh / var(--app-zoom, 1) - ${rect.top + 16}px)` }} role="dialog" aria-label="Choose date range">
          <Summary>
            <Pillbox $muted={!rangeStart}>{rangeStart ? fmtDay(rangeStart) : 'Start'}</Pillbox>
            <Arrow><i className="fas fa-arrow-right-long" /></Arrow>
            <Pillbox $muted={anchor ? true : !rangeEnd}>
              {anchor ? 'End' : rangeEnd ? fmtDay(rangeEnd) : 'End'}
            </Pillbox>
          </Summary>

          <Head>
            <NavBtn type="button" onClick={() => step(-1)} disabled={prevDisabled} aria-label="Previous month">
              <i className="fas fa-chevron-left" />
            </NavBtn>
            <HeadLabel>{MONTHS[view.m0]} {view.y}</HeadLabel>
            <NavBtn type="button" onClick={() => step(1)} disabled={nextDisabled} aria-label="Next month">
              <i className="fas fa-chevron-right" />
            </NavBtn>
          </Head>

          <Grid>
            {WEEKDAYS.map((w, i) => <Wk key={i}>{w}</Wk>)}
            {Array.from({ length: firstWeekday }).map((_, i) => <Blank key={`b${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const key = toKey(view.y, view.m0, day);
              const disabled = isDisabled(key);
              const isStart = key === rangeStart;
              const isEnd = key === rangeEnd;
              const single = rangeStart === rangeEnd;
              const inRange = key > rangeStart && key < rangeEnd;
              const cap = isStart || isEnd;
              // Band reaches toward the inside of the range from a cap; mid-range
              // days have the band on both sides.
              const bandLeft = inRange || (isEnd && !single);
              const bandRight = inRange || (isStart && !single);
              return (
                <Day
                  key={key}
                  type="button"
                  $cap={cap}
                  $capLeft={isStart}
                  $capRight={isEnd}
                  $inRange={inRange}
                  $bandLeft={bandLeft}
                  $bandRight={bandRight}
                  $today={key === todayKey}
                  $disabled={disabled}
                  disabled={disabled}
                  onMouseEnter={() => anchor && !disabled && setHover(key)}
                  onClick={() => pick(key)}
                >
                  <span>{day}</span>
                </Day>
              );
            })}
          </Grid>
        </Pop>,
        document.body
      )}
    </>
  );
};

export default DateRangeCalendar;
